"use client";

import { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-2">
        <div className="h-4 w-16 bg-muted animate-pulse" />
        <div className="h-4 w-16 bg-muted animate-pulse" />
        <div className="h-4 w-16 bg-muted animate-pulse" />
        <div className="h-4 w-16 bg-muted animate-pulse" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-10">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative bg-transparent hover:bg-transparent focus:bg-transparent text-foreground/90 hover:text-teal-600 focus:text-teal-600 text-[12px] font-default uppercase tracking-[0.2em] px-0 py-2 transition-colors duration-300">
                <span className="relative inline-block">
                  {item.label}
                  <span className="absolute -bottom-1 left-0 h-px w-0 bg-teal-600 transition-all duration-300 group-hover:w-full" />
                </span>
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 p-6">
        <div className="h-12 w-full bg-muted animate-pulse" />
        <div className="h-12 w-full bg-muted animate-pulse" />
        <div className="h-12 w-full bg-muted animate-pulse" />
      </div>;
  }
  return <nav className="flex flex-col">
      {menuItems.map((item, i) => <div key={item.id} data-index={i} className="border-b border-border">
          <Link to={item.href || "#"} className="block text-foreground hover:text-teal-600 text-3xl font-default font-bold uppercase tracking-tight py-5 px-2 transition-colors duration-300">
            {item.label}
          </Link>
        </div>)}
    </nav>;
}
export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  const marqueeText = "MS BIOTECHNOLOGY ◉ GLOBAL MEDICAL SOURCING — CLINICAL EVALUATION — STRATEGIC DISTRIBUTION — INNOVATIVE TECHNOLOGY PARTNERSHIPS —";
  return <section data-section-id="5152" id="header" className="sticky top-0 z-50 w-full bg-background text-foreground">
      <div className="sr-only">
        <h1>MS = Medical Solutions</h1>
        <div>
          <h2>Brand Promise</h2>
          <p>Innovating Medical Solutions for Better Life.</p>
        </div>
        <div>
          <h2>Core Values</h2>
          <p>Science • Innovation • Integrity • Partnership • Care</p>
        </div>
        <div>
          <h2>Slogan</h2>
          <p>Advancing Healthcare. Enriching Life.</p>
        </div>
      </div>

      <div className="h-6 w-full overflow-hidden border-b border-border bg-background flex items-center">
        <div className="flex whitespace-nowrap animate-[marquee_40s_linear_infinite] text-muted-foreground font-default text-[10px] tracking-[0.2em] uppercase">
          <span className="px-8">{marqueeText}</span>
          <span className="px-8">{marqueeText}</span>
          <span className="px-8">{marqueeText}</span>
          <span className="px-8">{marqueeText}</span>
        </div>
      </div>

      <div className={`w-full transition-all duration-[400ms] ease-out border-b ${scrolled ? "bg-background/80 backdrop-blur-md border-border" : "bg-background border-border"}`}>
        <div className="mx-auto max-w-7xl h-[72px] px-6 lg:px-10 flex items-center justify-between gap-6">
          <div className="flex items-center shrink-0">
            <Link to="/" aria-label="MS BIOTECHNOLOGY home" className="flex items-center gap-3">
              <WvcLogo className="h-12 w-auto text-blue-950" />
              <div className="flex flex-col leading-none">
                <div className="text-lg font-bold tracking-tight">
                  <span className="text-blue-950">MS BIOTECHNOLOGY</span>
                </div>
                <div className="text-[8px] text-muted-foreground tracking-[0.3em] font-semibold uppercase">
                  PREMIUM MEDICAL SOLUTIONS
                </div>
              </div>
            </Link>
          </div>

          <div className="hidden lg:flex flex-1 justify-center">
            <MenuProvider menu_id="16">
              <DesktopMenuConsumer />
            </MenuProvider>
          </div>

          <div className="flex items-center gap-3 shrink-0">
            <Button asChild variant="outline" className="hidden sm:inline-flex h-10 px-6 border-teal-600 text-teal-600 hover:bg-teal-50 font-default text-[12px] uppercase tracking-[0.2em] font-semibold rounded-lg transition-all">
              <Link to="#">Member Login</Link>
            </Button>

            <Button asChild className="hidden sm:inline-flex h-10 px-6 bg-teal-600 text-white hover:bg-teal-700 font-default text-[12px] uppercase tracking-[0.2em] font-semibold rounded-lg shadow-md transition-all">
              <Link to="/HomePage">Partner With Us</Link>
            </Button>

            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Open menu" className="lg:hidden h-10 w-10 text-foreground hover:text-teal-600 hover:bg-transparent">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-full sm:max-w-full bg-background text-foreground border-l border-border p-0 flex flex-col">
                <SheetTitle className="sr-only">Navigation Menu</SheetTitle>

                <div className="flex items-center justify-between px-6 h-[72px] border-b border-border">
                  <Link to="/" aria-label="MS BIOTECHNOLOGY home" onClick={() => setMobileOpen(false)} className="flex items-center gap-3">
                    <WvcLogo className="h-12 w-auto text-blue-950" />
                    <div className="flex flex-col leading-none">
                      <div className="text-lg font-bold tracking-tight">
                        <span className="text-blue-950">MS BIOTECHNOLOGY</span>
                      </div>
                      <div className="text-[8px] text-muted-foreground tracking-[0.3em] font-semibold uppercase">
                        PREMIUM MEDICAL SOLUTIONS
                      </div>
                    </div>
                  </Link>
                </div>

                <div className="flex-1 overflow-y-auto px-4 py-6">
                  <MenuProvider menu_id="16">
                    <MobileMenuConsumer />
                  </MenuProvider>
                </div>

                <div className="p-6 border-t border-border flex flex-col gap-3">
                  <Button asChild variant="outline" className="w-full h-12 border-teal-600 text-teal-600 hover:bg-teal-50 font-default text-[12px] uppercase tracking-[0.2em] font-semibold rounded-lg">
                    <Link to="#" onClick={() => setMobileOpen(false)}>
                      Member Login
                    </Link>
                  </Button>
                  <Button asChild className="w-full h-12 bg-teal-600 text-white hover:bg-teal-700 font-default text-[12px] uppercase tracking-[0.2em] font-semibold rounded-lg shadow-md">
                    <Link to="/HomePage" onClick={() => setMobileOpen(false)}>
                      Partner With Us
                    </Link>
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </section>;
}